import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MessageSquare, ArrowUpRight, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CONTACT } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactNewsletter() {
  const [contact, setContact] = useState({ name: "", email: "", subject: "", message: "" });
  const [contactLoading, setContactLoading] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);

  const submitContact = async (e) => {
    e.preventDefault();
    if (!contact.name || !contact.email || !contact.message) {
      toast.error("Please fill in name, email and message");
      return;
    }
    setContactLoading(true);
    try {
      await axios.post(`${API}/contact`, contact);
      toast.success("Message sent. We'll reply within 2 minutes.");
      setContact({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Could not send. Try again.");
    } finally {
      setContactLoading(false);
    }
  };

  const submitNewsletter = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    setNewsletterLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email: newsletterEmail });
      toast.success("Subscribed. Rate alerts incoming.");
      setNewsletterEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Invalid email.");
    } finally {
      setNewsletterLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#3B6FBF] font-bold mb-4">
                Get in touch
              </div>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl leading-[1.05] text-slate-900" data-testid="contact-title">
                Talk to a real human.
              </h2>
              <p className="mt-5 text-slate-600">
                Drop us a message and a real teammate will respond — average reply time under 2 minutes.
              </p>
            </div>

            <div className="space-y-3">
              <a
                href={`mailto:${CONTACT.email}`}
                className="flex items-center gap-4 p-5 rounded-2xl border border-slate-200 bg-white hover:border-[#3B6FBF]/40 hover:shadow-sm transition-all group"
                data-testid="contact-email-card"
              >
                <div className="w-11 h-11 rounded-xl bg-[#3B6FBF]/10 border border-[#3B6FBF]/20 flex items-center justify-center text-[#3B6FBF]">
                  <Mail className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">Email</div>
                  <div className="font-semibold text-slate-900">{CONTACT.email}</div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-slate-900" />
              </a>

              <a
                href={`tel:${CONTACT.phone}`}
                className="flex items-center gap-4 p-5 rounded-2xl border border-slate-200 bg-white hover:border-[#3B6FBF]/40 hover:shadow-sm transition-all group"
                data-testid="contact-phone-card"
              >
                <div className="w-11 h-11 rounded-xl bg-[#3B6FBF]/10 border border-[#3B6FBF]/20 flex items-center justify-center text-[#3B6FBF]">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">Phone</div>
                  <div className="font-semibold font-mono-data text-slate-900">{CONTACT.phone}</div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-slate-900" />
              </a>
            </div>

            <div className="rounded-2xl border border-[#3B6FBF]/30 bg-gradient-to-br from-[#3B6FBF]/10 to-transparent p-6 mt-2" data-testid="newsletter-card">
              <h3 className="font-display font-bold text-xl mb-1.5 text-slate-900">Stay on top of rates</h3>
              <p className="text-sm text-slate-600 mb-4">Get weekly market updates and feature drops. No spam, ever.</p>
              <form onSubmit={submitNewsletter} className="flex flex-col sm:flex-row gap-2" data-testid="newsletter-form">
                <Input
                  type="email"
                  placeholder="you@email.com"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  className="bg-white border-slate-300 text-slate-900 rounded-full px-5 h-12"
                  data-testid="newsletter-input"
                  required
                />
                <button
                  type="submit"
                  disabled={newsletterLoading}
                  className="inline-flex items-center justify-center gap-2 bg-[#3B6FBF] hover:bg-[#2f5ca3] disabled:opacity-60 text-white font-semibold rounded-full px-6 h-12 transition-all"
                  data-testid="newsletter-submit-btn"
                >
                  {newsletterLoading ? "Subscribing…" : <>Subscribe <Send className="w-4 h-4" /></>}
                </button>
              </form>
            </div>
          </div>

          <form
            onSubmit={submitContact}
            className="lg:col-span-3 rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-[0_10px_40px_-20px_rgba(15,23,42,0.15)]"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">Name</label>
                <Input
                  value={contact.name}
                  onChange={(e) => setContact({ ...contact, name: e.target.value })}
                  placeholder="Your name"
                  className="bg-white border-slate-300 text-slate-900 h-12 rounded-xl"
                  data-testid="contact-input-name"
                  required
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">Email</label>
                <Input
                  type="email"
                  value={contact.email}
                  onChange={(e) => setContact({ ...contact, email: e.target.value })}
                  placeholder="you@email.com"
                  className="bg-white border-slate-300 text-slate-900 h-12 rounded-xl"
                  data-testid="contact-input-email"
                  required
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">Subject</label>
              <Input
                value={contact.subject}
                onChange={(e) => setContact({ ...contact, subject: e.target.value })}
                placeholder="How can we help?"
                className="bg-white border-slate-300 text-slate-900 h-12 rounded-xl"
                data-testid="contact-input-subject"
              />
            </div>
            <div className="mt-4">
              <label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">Message</label>
              <Textarea
                value={contact.message}
                onChange={(e) => setContact({ ...contact, message: e.target.value })}
                placeholder="Tell us a bit about what you need…"
                rows={6}
                className="bg-white border-slate-300 text-slate-900 rounded-xl resize-none"
                data-testid="contact-input-message"
                required
              />
            </div>
            <button
              type="submit"
              disabled={contactLoading}
              className="mt-6 inline-flex items-center justify-center gap-2 bg-[#3B6FBF] hover:bg-[#2f5ca3] disabled:opacity-60 text-white font-semibold rounded-full px-7 h-12 transition-all"
              data-testid="contact-submit-btn"
            >
              {contactLoading ? "Sending…" : <>Send message <Send className="w-4 h-4" /></>}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
